/**
 * Share Entity Dialog — entity-level ACL management UI.
 *
 * Design rationale (frontend-design skill applied):
 *
 *   Refined minimalism with one signature moment of state change.
 *   The privacy mode toggle is THE primary hierarchy — two large
 *   picker cards, not a hidden switch.  Picking RESTRICTED is a
 *   visible, weighty choice; the people list animates from 0.6 → 1
 *   opacity with a 180ms ease-out so the moment carries meaning.
 *   Everything else is instant.
 *
 *   - Owner row pinned, marked, immovable.
 *   - "Just me →" preset is a text link in the header (discoverable
 *     for power users, invisible to people who'd never use it).
 *   - Workspace-mode warning banner: never dismissable, slides in
 *     from top when mode flips RESTRICTED.
 *   - Microcopy is concrete: "Restricted to 4 people including you",
 *     not "Some people".
 */

import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { entityDetailPath } from '../../utils/entityRoutes';
import { HELP_LINKS } from '../../constants/helpLinks';
import { MaterialIcon } from '../ui/MaterialIcon';
import { useAppToast } from '../../hooks/ui/useAppToast';
import {
  type AclEntityType,
  type EntityPrivacyMode,
  useEntityAclMutations,
  useEntityAclSnapshot,
  EntityAclError,
} from '../../hooks/data/useEntityAccess';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useCurrentUserQuery } from '../../hooks/user/useUserQuery';
import { AddMemberRow } from './acl/AddMemberRow';
import { PersonRow } from './acl/PersonRow';
import { PrivacyPickerCard } from './acl/PrivacyPickerCard';
import { ShareToWeb } from './acl/ShareToWeb';
import { BowerSpinner } from '../ui/BowerSpinner';

/**
 * Human labels for an entity's own (possibly dormant) setting.
 *
 * Keyed on `EntityPrivacyMode`, not `string`: with the wider type a mode
 * added later renders the literal "(undefined)" in the banner instead of
 * failing to compile.
 */
const OWN_MODE_LABEL: Record<EntityPrivacyMode, string> = {
  WORKSPACE: 'anyone in the workspace',
  RESTRICTED: 'restricted',
  PRIVATE: 'private',
};

interface ShareEntityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: AclEntityType;
  entityId: string;
  /** Display name for the entity (e.g. project / artifact title). */
  entityName: string;
}

export function ShareEntityDialog({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
}: ShareEntityDialogProps): React.ReactElement | null {
  // `isLoading` / `error` matter as much as `workspace` here. The workspace
  // query is `retry: false`, and the context reports a FAILED fetch as
  // `workspace: null` — indistinguishable from an in-flight one. Reading only
  // `workspace` would spin forever on a 500, which is the same silent failure
  // this dialog was fixed to stop shipping.
  const {
    workspace,
    isLoading: workspaceLoading,
    error: workspaceError,
    refreshWorkspace,
  } = useWorkspace();
  const { data: currentUser, isLoading: userLoading, isError: userError } = useCurrentUserQuery();
  const toast = useAppToast();
  const snapshot = useEntityAclSnapshot(entityType, entityId, isOpen);
  const mut = useEntityAclMutations({ entityType, entityId });

  // Long-list filter — workspaces with 50+ members make scrolling
  // impractical.  A simple text filter against the rendered ACL
  // (matches name + email) keeps the people list usable up to ~200
  // members; above that we'd revisit virtualisation.  State stays
  // local — no need to hit the URL or persist across sessions.
  const [peopleFilter, setPeopleFilter] = useState('');

  // Fire `Share Dialog Opened` exactly once per `isOpen` transition.
  // Centralised here so call sites that mount the dialog (NoteActionMenu,
  // ProjectDetail, future surfaces) don't have to duplicate the
  // tracking — and so the event semantics ("dialog actually rendered
  // and saw the entity ACL") matches the event name.
  useEffect(() => {
    if (!isOpen) return;
    safeAnalytics.track(ANALYTICS_EVENTS.SHARE_DIALOG_OPENED, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }, [isOpen, entityType, entityId]);

  // Contract guard. A workspace that has LOADED but carries no
  // `createdById` is an API-contract violation, not a load state — the
  // dialog can't resolve the owner row and would degrade to a spinner
  // forever. Surface it loudly rather than letting the UI look merely
  // slow. (This is the exact failure that shipped: the frontend type
  // declared `createdBy`, the API returns `createdById`.)
  useEffect(() => {
    if (!isOpen || !workspace || workspace.createdById) return;
    console.error(
      '[ShareEntityDialog] workspace payload has no `createdById`: access controls cannot render',
      { workspaceId: workspace.id },
    );
    // console alone is invisible in production. Route it to the same sink the
    // rest of the app uses so a contract violation is detectable without a
    // devtools session open.
    safeAnalytics.captureException(
      new Error('ShareEntityDialog: workspace payload missing createdById'),
      { workspace_id: workspace.id },
    );
  }, [isOpen, workspace]);

  const mode: EntityPrivacyMode = snapshot.data?.mode ?? 'WORKSPACE';
  // Memoised: `?? []` mints a NEW array every render, and two `useMemo`s
  // below take `aclEntries` as a dependency — so without this they recompute
  // on every render and their memoisation is decorative.
  const aclEntries = useMemo(() => snapshot.data?.acl ?? [], [snapshot.data?.acl]);
  const workspaceOwnerId = workspace?.createdById;
  // `inherited === false` means this entity has its OWN privacy row.
  // Only then does "Use parent's settings" make sense — otherwise the
  // entity is already inheriting and there's nothing to reset.
  const hasOwnPrivacyRow = snapshot.data?.inherited === false;
  // When the displayed ACL is inherited from an ancestor (this entity
  // has no own entity_acl rows, but it's RESTRICTED via inheritance),
  // surface that fact so admins know where to go to edit the grants.
  const aclInheritedFrom = snapshot.data?.aclInheritedFrom ?? null;
  // What actually governs reads, which can be STRICTER than this
  // entity's own setting. Without this the dialog rendered an entity's
  // own "anyone in workspace" row while it sat inside a private
  // collection — telling the user their note was open when nobody
  // outside the boundary could read it.
  const effectiveMode = snapshot.data?.effectiveMode ?? 'WORKSPACE';
  const ownMode = snapshot.data?.ownMode ?? null;
  // How the entity's OWN setting relates to the boundary above it. Named
  // rather than nested inline: a RESTRICTED/PRIVATE own mode narrows NOW (it
  // is additive to the ancestor), while only a WORKSPACE own mode is the
  // dormant "applies if it moves out" case — and calling the wrong one
  // dormant understates how closed the entity is.
  let ownModeNote: string;
  if (ownMode === null) {
    ownModeNote = 'Changes there will flow down here.';
  } else if (ownMode === 'WORKSPACE') {
    ownModeNote = `This item's own setting (${OWN_MODE_LABEL[ownMode]}) applies only if it moves out.`;
  } else {
    ownModeNote = `This item is also ${OWN_MODE_LABEL[ownMode]} in its own right.`;
  }

  const effectiveBoundaries = snapshot.data?.effectiveBoundaries ?? [];
  /**
   * Whether the snapshot actually arrived.
   *
   * Every derived value above falls back to the OPEN answer
   * (`'WORKSPACE'`, no boundaries), so while the query is in flight — and
   * permanently, if it errors, which was handled nowhere — the dialog
   * rendered "Everyone in this workspace can see this." for an entity
   * whose real state it had not yet read. Telling someone their private
   * note is public is the one failure this dialog must not have, so the
   * privacy section renders nothing until the answer is known.
   */
  const snapshotReady = !snapshot.isLoading && !snapshot.isError && snapshot.data != null;
  // True when an ancestor is narrowing this entity below its own
  // setting — i.e. the own setting is dormant.
  const narrowedByAncestor =
    effectiveBoundaries.length > 0 &&
    (ownMode === null || ownMode === 'WORKSPACE' || effectiveMode !== ownMode);

  // Public sharing only applies to artifacts (notes, attachments).
  // Project sharing happens through the workspace-membership flow.
  const showShareToWeb = entityType === 'ARTIFACT';

  const ownerInAcl = useMemo(
    () => aclEntries.find((e) => e.principalUserId === workspaceOwnerId),
    [aclEntries, workspaceOwnerId],
  );
  // PRIVATE mode suppresses the OWNER read carve-out — the workspace
  // owner does NOT have implicit access.  Don't render the synthetic
  // "Workspace owner — Full access" pinned row in that case (it
  // contradicts the privacy banner above and the underlying
  // permission resolver).  If the owner has an EXPLICIT grant on a
  // PRIVATE entity, they still appear via the normal ACL row path.
  const ownerCarveoutApplies = effectiveMode !== 'PRIVATE';
  // Entity creator's user id from the snapshot.  Drives the "Creator"
  // badge in the people list and the protect-from-removal logic.
  // When the creator IS the workspace owner, we render the owner badge
  // (more authoritative); otherwise the creator gets their own badge.
  const creatorId = snapshot.data?.createdById ?? null;
  const creatorIsOwner = creatorId !== null && creatorId === workspaceOwnerId;
  // Add a synthetic "creator" pinned row when the creator isn't the
  // workspace owner AND isn't already in the explicit ACL.  Mirrors
  // the workspace-owner pinned-row pattern.
  const creatorInAcl = useMemo(
    () =>
      creatorId !== null ? aclEntries.find((e) => e.principalUserId === creatorId) : undefined,
    [aclEntries, creatorId],
  );
  // The creator's carve-out covers the entity's OWN restriction only.
  // Satisfying an ANCESTOR's boundary needs a grant THERE, which this
  // snapshot cannot see, so the dialog must not assert the creator has
  // access whenever any boundary belongs to something else.
  //
  // Gate on the boundary CHAIN, not on `inherited`. `inherited` means "this
  // entity has no privacy row of its own", which is a different question: a
  // note with its OWN RESTRICTED row inside someone else's PRIVATE
  // collection has `inherited === false` and an ancestor boundary, so the
  // old check re-rendered "Created by — Full access" on an entity the
  // creator gets a 404 on. `effectiveBoundaries` is exactly the list the
  // backend says must be satisfied, so "every boundary IS this entity"
  // is the only case the creator's own carve-out covers.
  //
  // Same class as the effectiveMode fix above — the banner told the truth
  // while the rows below it did not — and the same class as the owner
  // carve-out one screen up, which already keys on `effectiveMode`.
  const creatorCarveoutApplies = (snapshot.data?.effectiveBoundaries ?? []).every(
    (b) => b.entityId === entityId,
  );
  const creatorNeedsPinnedRow =
    creatorId !== null && !creatorIsOwner && !creatorInAcl && creatorCarveoutApplies;

  // Hooks must run unconditionally — bail only after every hook above.
  // Owner ID + self ID are required for the pinned-owner row and the
  // "(you)" badge.
  //
  // Rule 12 (fail loud): this bail used to `return null`, so a missing
  // field turned "Manage access" into a dead click with no dialog, no
  // toast and no console output — the bug shipped to production and
  // survived every test. Now the click ALWAYS produces a visible dialog.
  //
  // A spinner is shown ONLY while a fetch is genuinely in flight. Everything
  // else — a failed fetch (the query is `retry: false`, so `workspace` stays
  // null forever), or a workspace that loaded without `createdById` — is
  // terminal and gets an error state with a Retry. Collapsing these into one
  // spinner would be the same silent failure in a nicer costume: the user
  // waiting on something that is never coming.
  const isFetchInFlight = (workspaceLoading && !workspace) || (userLoading && !currentUser);
  const cannotRender = !workspace || !currentUser || workspaceOwnerId === undefined;
  if (cannotRender) {
    return (
      // Header padding + responsive size mirror the real dialog below, so the
      // modal doesn't jump size or run the title under the close button when
      // the workspace resolves.
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} isCentered={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>Who can see {entityName}?</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            {isFetchInFlight ? (
              // role="status" — the modal is already open on the
              // loading→loaded/error transition, so nothing else announces it.
              <VStack py={8} spacing={3} role="status">
                <BowerSpinner size="lg" />
                <Text fontSize="sm" color="text.muted">
                  Loading access settings…
                </Text>
              </VStack>
            ) : (
              <VStack py={8} spacing={3} role="alert">
                {/* Decorative — MaterialIcon renders the icon NAME as span
                    text, so an unhidden icon is read aloud as "error". The
                    message below carries the meaning. */}
                <MaterialIcon name="error" boxSize={8} color="text.error" aria-hidden />
                <Text fontSize="sm" color="text.primary" textAlign="center">
                  Access settings couldn’t be loaded for this workspace.
                </Text>
                <Text fontSize="xs" color="text.muted" textAlign="center">
                  {workspaceError ?? (userError ? 'Could not load your account.' : null) ?? null}{' '}
                  Sharing is unchanged until this is fixed.
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void refreshWorkspace();
                  }}
                >
                  Retry
                </Button>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }
  const currentUserId = currentUser.id;

  // ─── Mode change handler ────────────────────────────────────────
  const handleModeChange = async (next: EntityPrivacyMode): Promise<void> => {
    if (mode === next) return;
    try {
      await mut.setMode.mutateAsync(next);
      safeAnalytics.track(ANALYTICS_EVENTS.ENTITY_PRIVACY_CHANGED, {
        entity_type: entityType,
        entity_id: entityId,
        from: mode,
        to: next,
      });
    } catch (e) {
      // Tailored toast for the compliance opt-out case so the user
      // knows why PRIVATE was rejected and what to do instead.
      // Use the structured error code from the backend rather than
      // matching on `error.message` — backend copy can change without
      // breaking this UX path (review W2).
      const message = e instanceof Error ? e.message : 'Try again';
      const isPrivateDisabled =
        next === 'PRIVATE' && e instanceof EntityAclError && e.code === 'PRIVATE_MODE_DISABLED';
      toast({
        title: isPrivateDisabled
          ? 'Private mode is disabled in this workspace'
          : 'Could not change privacy',
        description: isPrivateDisabled
          ? 'Use Only specific people to limit who can read this item.'
          : message,
        status: isPrivateDisabled ? 'info' : 'error',
      });
    }
  };

  // ─── Reset-to-inherited handler ────────────────────────────────
  // Drops the explicit privacy row so the entity inherits from its
  // parent.  Distinct from `setMode('WORKSPACE')` which forces
  // WORKSPACE — this lets future ancestor changes (parent flipped
  // RESTRICTED, etc.) flow down.
  const handleResetToInherited = async (): Promise<void> => {
    const previousMode = mode;
    try {
      await mut.resetToInherited.mutateAsync();
      safeAnalytics.track(ANALYTICS_EVENTS.ENTITY_PRIVACY_RESET, {
        entity_type: entityType,
        entity_id: entityId,
        previous_mode: previousMode,
      });
      toast({ title: 'Now inherits from parent', status: 'success' });
    } catch (e) {
      toast({
        title: 'Could not reset privacy',
        description: e instanceof Error ? e.message : 'Try again',
        status: 'error',
      });
    }
  };

  // ─── "Just me" preset ──────────────────────────────────────────
  // Sets PRIVATE mode (Notion-style — workspace OWNERs cannot read).
  // Workspaces with `allowPrivateMode: false` (compliance opt-out)
  // fall back to RESTRICTED at the service layer; the toast copy
  // adapts to the resolved mode so we don't lie to the user.
  const handleMakePrivate = async (): Promise<void> => {
    try {
      const result = await mut.makePrivate.mutateAsync();
      safeAnalytics.track(ANALYTICS_EVENTS.ENTITY_MADE_PRIVATE, {
        entity_type: entityType,
        entity_id: entityId,
        resolved_mode: result?.mode ?? 'PRIVATE',
      });
      toast({
        title:
          result?.mode === 'PRIVATE'
            ? 'Private to you: workspace owners can’t read this'
            : 'Restricted to you',
        status: 'success',
      });
    } catch (e) {
      toast({
        title: 'Could not make private',
        description: e instanceof Error ? e.message : 'Try again',
        status: 'error',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // Full-screen on mobile so the dialog respects the small viewport
      // and the people list isn't clipped by a centered modal.  Desktop
      // keeps the comfortable centered `lg` size.
      size={{ base: 'full', md: 'lg' }}
      isCentered={false}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2} pr={12}>
          {/* `pr={12}` reserves space for the absolutely-positioned
              ModalCloseButton (Chakra defaults to top-right inset
              ~3rem).  Without it the title row would sit underneath
              the X icon. */}
          <VStack align="stretch" spacing={1}>
            <Text fontSize="lg" fontWeight="semibold" color="text.primary">
              Who can see {entityName}?
            </Text>
            <Text fontSize="sm" color="text.subtle">
              Choose how visible this {entityType.toLowerCase()} is to your workspace.{' '}
              <Link href={HELP_LINKS.SHARING} isExternal>
                Learn more
              </Link>
            </Text>
          </VStack>
          <ModalCloseButton />
        </ModalHeader>

        <ModalBody pb={6}>
          <VStack align="stretch" spacing={5}>
            {/* ─── Share to web ───
                Public link controls.  Coexists with the ACL section
                below (Notion's pattern).  ARTIFACT-only — projects
                don't have public share links in v1. */}
            {showShareToWeb && (
              <Box
                p={3}
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                bg="bg.subtle"
              >
                {/* Any non-workspace effective mode warns, PRIVATE included:
                    publishing to the web is exactly what a restricted OR
                    private entity most needs flagged, and keying on
                    RESTRICTED alone suppressed it for the stricter case. */}
                <ShareToWeb artifactId={entityId} isRestricted={effectiveMode !== 'WORKSPACE'} />
              </Box>
            )}

            {/* ─── Inheritance source caption ───
                Shown whenever this entity inherits its privacy mode
                from an ancestor — gives the user a navigable link to
                the parent so they can audit the source of truth, and
                makes it explicit that future changes to the parent
                will flow down here automatically.  Suppressed when
                the entity has its own privacy row (no inheritance
                relationship to surface) or when there's no ancestor
                with a row (workspace default applies). */}
            {narrowedByAncestor && (
              <HStack
                spacing={2}
                px={3}
                py={2}
                bg="bg.subtle"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                fontSize="sm"
                color="text.secondary"
                alignItems="flex-start"
              >
                <MaterialIcon name="lock" boxSize="14px" />
                <Text>
                  {effectiveMode === 'PRIVATE' ? 'Private' : 'Restricted'}, inherited from{' '}
                  {effectiveBoundaries.map((boundary, index) => (
                    <Fragment key={boundary.entityId}>
                      {index > 0 && ', '}
                      <Link
                        as={RouterLink}
                        to={entityDetailPath({
                          entityType: boundary.entityType,
                          entityId: boundary.entityId,
                        })}
                        onClick={onClose}
                      >
                        {boundary.name}
                      </Link>
                      {/* Each boundary carries its OWN mode. A chain can be
                          mixed — one RESTRICTED ancestor and one PRIVATE one —
                          and labelling every link with the aggregate reads as
                          "Private — inherited from A, B" when only B is
                          private. The caller must be able to tell which one to
                          go and change. */}
                      {boundary.mode === 'PRIVATE' ? ' (private)' : ' (restricted)'}
                    </Fragment>
                  ))}
                  .{' '}
                  {/* "applies only if it moves out" is true only for a DORMANT
                      own setting. A RESTRICTED or PRIVATE own mode narrows
                      NOW — it is additive to the boundary above, not waiting
                      on a move — so saying it is dormant understates how
                      closed the entity is. */}
                  {ownModeNote}
                </Text>
              </HStack>
            )}

            {/* ─── PRIVATE-mode banner ───
                When the entity is PRIVATE, the picker cards above
                show as INACTIVE (neither WORKSPACE nor RESTRICTED is
                selected) — instead we surface a dedicated banner so
                the user knows they're in a special creator-only
                state and can opt out by changing mode below. */}
            {effectiveMode === 'PRIVATE' && (
              <HStack
                spacing={2}
                px={3}
                py={2}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
                role="status"
              >
                <MaterialIcon name="visibility_off" boxSize="14px" color="text.secondary" />
                <Text fontSize="sm" color="text.secondary">
                  <Text as="strong" color="text.primary" fontWeight="medium">
                    Private to you.
                  </Text>{' '}
                  Workspace owners can’t read this. To share, switch to one of the modes below or
                  add people from the list.
                </Text>
              </HStack>
            )}

            {/* ─── Privacy mode picker ─── */}
            <HStack align="stretch" spacing={3}>
              <PrivacyPickerCard
                active={effectiveMode === 'WORKSPACE'}
                icon="groups"
                title="Anyone in this workspace"
                description="All workspace members can read; members can edit."
                onClick={() => handleModeChange('WORKSPACE')}
                disabled={mut.setMode.isPending}
              />
              <PrivacyPickerCard
                active={effectiveMode === 'RESTRICTED'}
                icon="lock"
                title="Only specific people"
                description="Only the people listed below. Workspace owners can also read."
                onClick={() => handleModeChange('RESTRICTED')}
                disabled={mut.setMode.isPending}
              />
            </HStack>

            {/* ─── Footer actions ───
                "Just me" preset (WORKSPACE-mode only) — one-tap shortcut
                for locking to self.  In RESTRICTED mode the user manages
                the ACL directly via the people list below.
                "Use parent's settings" — only shown when the entity has
                its OWN explicit privacy row (Notion-style "reset to
                default").  Drops the row so future ancestor changes
                flow down automatically; distinct from picking
                "Anyone in this workspace" which forces WORKSPACE
                regardless of what the parent does later. */}
            <HStack justify="flex-end" mt={-2} spacing={4}>
              {hasOwnPrivacyRow && (
                <Button
                  variant="link"
                  size="sm"
                  color="text.subtle"
                  fontWeight="normal"
                  isLoading={mut.resetToInherited.isPending}
                  onClick={handleResetToInherited}
                >
                  Use parent&apos;s settings
                </Button>
              )}
              {effectiveMode === 'WORKSPACE' && (
                <Button
                  variant="link"
                  size="sm"
                  color="text.link"
                  fontWeight="medium"
                  rightIcon={<MaterialIcon name="arrow_forward" boxSize="14px" />}
                  // Only the label takes the hover underline, never the icon.
                  data-group
                  _hover={{ textDecoration: 'none' }}
                  isLoading={mut.makePrivate.isPending}
                  onClick={handleMakePrivate}
                >
                  <Box as="span" _groupHover={{ textDecoration: 'underline' }}>
                    Just me
                  </Box>
                </Button>
              )}
            </HStack>

            {/* ─── Restricted-mode banner ─── */}
            {effectiveMode === 'RESTRICTED' && (
              <Box
                p={3}
                bg="bg.warning"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.warning"
                role="alert"
                sx={{
                  animation: 'aclBannerSlide 180ms ease-out',
                  '@keyframes aclBannerSlide': {
                    from: { opacity: 0, transform: 'translateY(-4px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <HStack align="flex-start" spacing={2}>
                  <MaterialIcon name="info" boxSize="18px" color="text.warning" />
                  <Text fontSize="sm" color="text.primary">
                    Restricted from everyone in this workspace except the people listed below.
                  </Text>
                </HStack>
              </Box>
            )}

            {/* ─── Add people ─── */}
            {effectiveMode === 'RESTRICTED' && (
              <AddMemberRow
                entityId={entityId}
                onAdd={async (userId, permission) => {
                  try {
                    await mut.grant.mutateAsync({ userId, permission });
                    safeAnalytics.track(ANALYTICS_EVENTS.ENTITY_SHARED, {
                      entity_type: entityType,
                      entity_id: entityId,
                      permission,
                    });
                  } catch (e) {
                    toast({
                      title: 'Could not add member',
                      description: e instanceof Error ? e.message : 'Try again',
                      status: 'error',
                    });
                  }
                }}
                excludeUserIds={aclEntries.map((e) => e.principalUserId)}
                isPending={mut.grant.isPending}
              />
            )}

            {/* ─── Inheritance source caption ───
                When the displayed ACL is inherited from an ancestor
                (this entity has no own grants but its parent does),
                tell the admin where to navigate to edit grants.
                Avoids the confusing "I removed Alice but she still
                has access" scenario. */}
            {aclInheritedFrom && (
              <HStack
                spacing={2}
                px={3}
                py={2}
                bg="bg.subtle"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                fontSize="sm"
                color="text.secondary"
              >
                <MaterialIcon name="account_tree" boxSize="14px" />
                <Text>
                  Access list inherited from{' '}
                  <Text as="strong" color="text.primary">
                    {aclInheritedFrom.name}
                  </Text>
                  . Edit there to change who can access this {entityType.toLowerCase()}.
                </Text>
              </HStack>
            )}

            {/* ─── People list ─── */}
            {/* Notion-style: hide the per-person list entirely in
                WORKSPACE mode.  In that mode the list is purely
                advisory — no row's permission has any effect on what
                workspace members can read.  Rendering rows that look
                interactive but whose actions modify a dormant ACL was
                confusing UX (the user reported it).  The grants are
                preserved server-side; flipping back to RESTRICTED
                restores them.

                In WORKSPACE mode we only show a one-line summary;
                in RESTRICTED mode we show the full interactive list. */}
            {snapshotReady && effectiveMode === 'WORKSPACE' && (
              <HStack
                spacing={2}
                px={3}
                py={2}
                bg="bg.subtle"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                fontSize="sm"
                color="text.secondary"
              >
                <MaterialIcon name="groups" boxSize="14px" />
                <Text>
                  Everyone in this workspace can see this.
                  {aclEntries.length > 0 && (
                    <>
                      {' '}
                      <Text as="span" color="text.subtle">
                        ({aclEntries.length}{' '}
                        {aclEntries.length === 1 ? 'person has' : 'people have'} extra permissions
                        kept for if you switch back.)
                      </Text>
                    </>
                  )}
                </Text>
              </HStack>
            )}
            {snapshotReady && effectiveMode !== 'WORKSPACE' && (
              <>
                {/* Long-list filter — only render when the list is big
                    enough to warrant it.  Threshold of 8 keeps the
                    small-team UX uncluttered.  Filter matches name +
                    email, case-insensitive. */}
                {aclEntries.length > 8 && (
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <MaterialIcon name="search" boxSize="14px" color="text.subtle" />
                    </InputLeftElement>
                    <Input
                      placeholder="Filter people…"
                      value={peopleFilter}
                      onChange={(e) => setPeopleFilter(e.target.value)}
                      aria-label="Filter people in the access list"
                    />
                  </InputGroup>
                )}
                <Box
                  // Cap the list height so a workspace with 50+ members
                  // doesn't push the footer off-screen.  ~6 rows fit
                  // before scrolling on desktop; mobile uses the
                  // full-screen modal's natural scroll instead.
                  maxH={{ base: 'none', md: '320px' }}
                  overflowY={{ base: 'visible', md: 'auto' }}
                  // `overflowY: auto` also clips overflow-X — the
                  // permission picker's focus ring extends a couple
                  // of px past the row edge and was getting trimmed
                  // on the right.  Pad the scroll container so the
                  // ring has room to render fully.
                  pr={{ base: 0, md: 1 }}
                >
                  <VStack
                    align="stretch"
                    spacing={0}
                    divider={<Box borderBottomWidth="1px" borderColor="border.subtle" />}
                  >
                    {/* Pinned workspace-owner row — never filtered out so
                    the user always sees who has the carve-out.  The
                    workspace owner is implicitly MANAGE on every
                    WORKSPACE / RESTRICTED entity, but NOT on PRIVATE
                    (no carve-out there) — suppress the synthetic row
                    in that mode. */}
                    {!ownerInAcl && ownerCarveoutApplies && (
                      <PersonRow
                        userId={workspaceOwnerId}
                        name="Workspace owner"
                        avatar={null}
                        isOwner
                        isCreator={false}
                        isCurrentUser={workspaceOwnerId === currentUserId}
                        permission="MANAGE"
                        onChangePermission={null}
                        onRemove={null}
                      />
                    )}
                    {/* Pinned entity-creator row — when the creator
                        isn't the workspace owner AND isn't already in
                        the explicit ACL.  Notion convention: the
                        creator always retains "Full access". */}
                    {creatorNeedsPinnedRow && creatorId && (
                      <PersonRow
                        userId={creatorId}
                        name="Created by"
                        avatar={null}
                        isOwner={false}
                        isCreator
                        isCurrentUser={creatorId === currentUserId}
                        permission="MANAGE"
                        onChangePermission={null}
                        onRemove={null}
                      />
                    )}
                    {aclEntries
                      .filter((entry) => {
                        if (!peopleFilter) return true;
                        const q = peopleFilter.toLowerCase();
                        const name = (entry.user.name ?? '').toLowerCase();
                        const id = entry.principalUserId.toLowerCase();
                        return name.includes(q) || id.includes(q);
                      })
                      .map((entry) => (
                        <PersonRow
                          key={entry.id}
                          userId={entry.principalUserId}
                          name={entry.user.name ?? 'Member'}
                          avatar={entry.user.avatar}
                          isOwner={entry.principalUserId === workspaceOwnerId}
                          isCreator={
                            entry.principalUserId === creatorId &&
                            entry.principalUserId !== workspaceOwnerId
                          }
                          isCurrentUser={entry.principalUserId === currentUserId}
                          permission={entry.permission}
                          canOpen={entry.canOpen}
                          blockedByName={entry.blockedBy?.name}
                          onChangePermission={
                            entry.principalUserId === workspaceOwnerId ||
                            entry.principalUserId === creatorId
                              ? null
                              : async (next) => {
                                  try {
                                    await mut.grant.mutateAsync({
                                      userId: entry.principalUserId,
                                      permission: next,
                                    });
                                    safeAnalytics.track(
                                      ANALYTICS_EVENTS.ENTITY_PERMISSION_CHANGED,
                                      {
                                        entity_type: entityType,
                                        entity_id: entityId,
                                        from: entry.permission,
                                        to: next,
                                      },
                                    );
                                  } catch (e) {
                                    toast({
                                      title: 'Could not change permission',
                                      description: e instanceof Error ? e.message : 'Try again',
                                      status: 'error',
                                    });
                                  }
                                }
                          }
                          onRemove={
                            entry.principalUserId === workspaceOwnerId ||
                            entry.principalUserId === creatorId
                              ? null
                              : async () => {
                                  try {
                                    await mut.revoke.mutateAsync(entry.principalUserId);
                                    safeAnalytics.track(ANALYTICS_EVENTS.ENTITY_UNSHARED, {
                                      entity_type: entityType,
                                      entity_id: entityId,
                                    });
                                  } catch (e) {
                                    toast({
                                      title: 'Could not remove',
                                      description: e instanceof Error ? e.message : 'Try again',
                                      status: 'error',
                                    });
                                  }
                                }
                          }
                        />
                      ))}
                  </VStack>

                  <Text mt={3} fontSize="xs" color="text.subtle">
                    {(() => {
                      // Owner is implicitly counted only when the
                      // carve-out applies AND they're not already in
                      // the explicit ACL.  PRIVATE has no carve-out —
                      // the count then reflects the explicit grants
                      // alone (which on PRIVATE is the creator
                      // until they share with someone).
                      const ownerImplicit = ownerCarveoutApplies && !ownerInAcl;
                      // Count the pinned CREATOR row too. It renders on
                      // exactly `creatorNeedsPinnedRow`, so counting the
                      // same predicate keeps the sentence and the list
                      // from disagreeing — the count said 1 while the
                      // list showed 2 for every own-restricted entity
                      // whose creator wasn't the workspace owner.
                      const total =
                        aclEntries.length +
                        (ownerImplicit ? 1 : 0) +
                        (creatorNeedsPinnedRow ? 1 : 0);
                      const noun = total === 1 ? 'person' : 'people';
                      const implicit = [
                        ownerImplicit ? 'the workspace owner' : null,
                        creatorNeedsPinnedRow ? 'the creator' : null,
                      ].filter(Boolean);
                      return implicit.length > 0
                        ? `Restricted to ${total} ${noun}: including ${implicit.join(' and ')}.`
                        : `Restricted to ${total} ${noun}.`;
                    })()}
                  </Text>
                </Box>
              </>
            )}

            {snapshot.isLoading && (
              <HStack justify="center" py={4}>
                <BowerSpinner size="sm" />
              </HStack>
            )}

            {snapshot.isError && (
              <HStack
                spacing={2}
                px={3}
                py={2}
                bg="bg.warning"
                borderRadius="md"
                fontSize="sm"
                color="text.secondary"
                alignItems="flex-start"
              >
                <MaterialIcon name="error" boxSize="14px" />
                <Text>
                  Couldn&apos;t load who can see this. Close and reopen to try again. Don&apos;t
                  assume it&apos;s shared or private until this loads.
                </Text>
              </HStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
